import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import PageHead from "../components/PageHead";
import ParticleField from "../components/ParticleField";
import Reveal from "../components/Reveal";
import { PrimaryButton } from "../components/Buttons";
import { heroBgStyle } from "../lib/heroBg";
import { services } from "../data/site";

export default function Services() {
  return (
    <main data-testid="services-page">
      <PageHead
        title="Services | Digital Archdeacon Media Ltd"
        description="Comprehensive ICT and media solutions: software, websites, video production, content creation, media consulting, and printing & publishing."
      />

      <section className="relative pt-36 pb-12 overflow-hidden" style={heroBgStyle} data-testid="services-hero">
        <ParticleField density={45} />
        <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
        <div className="container-x relative">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Our services</span>
            <h1 className="h-display text-[clamp(3rem,8vw,7rem)] mt-3">
              Built around <span className="text-gradient-blue">your goals</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#CBD5E1]">
              Comprehensive ICT and media solutions, designed around your goals.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="bg-navy-gradient">
      {services.map((s, idx) => {
        const Icon = s.icon;
        const reverse = idx % 2 === 1;
        return (
          <section
            key={s.id}
            id={s.id}
            className="py-20 lg:py-24 border-t border-white/5"
            data-testid={`service-section-${s.id}`}
          >
            <div className="container-x">
              <div
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  reverse ? "lg:[&>div:first-child]:order-2" : ""
                }`}
              >
                <Reveal>
                  <div>
                    <div className="inline-flex items-center gap-3 rounded-full border border-electric/30 bg-electric/10 pl-2 pr-4 py-1.5">
                      <span className="grid place-items-center h-7 w-7 rounded-full bg-electric/20 text-electric">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="text-xs uppercase tracking-[0.2em] text-electric font-medium">
                        Service {String(idx + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h2 className="h-display text-5xl md:text-6xl mt-5">
                      {s.title}
                    </h2>
                    <p className="mt-5 text-[#CBD5E1] text-lg leading-relaxed">{s.long}</p>

                    <ul className="mt-8 space-y-3">
                      {s.deliverables.map((d) => (
                        <li key={d} className="flex items-start gap-3 text-[15px]">
                          <span className="mt-1 grid place-items-center h-5 w-5 rounded-full bg-electric/15 text-electric">
                            <Check className="h-3 w-3" />
                          </span>
                          <span className="text-white/90">{d}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-9 flex flex-wrap gap-4">
                      <Link
                        to={`/services/${s.id}`}
                        data-testid={`service-${s.id}-detail`}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-4 text-sm md:text-base font-semibold text-white hover:border-electric hover:bg-electric/10 transition-all"
                      >
                        Explore {s.title}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                      <PrimaryButton
                        to="/request-quote"
                        testId={`service-${s.id}-cta`}
                      >
                        Get a Quote
                      </PrimaryButton>
                    </div>
                  </div>
                </Reveal>

                <Reveal delay={120}>
                  <div className="relative aspect-square max-w-[480px] mx-auto rounded-[28px] overflow-hidden border border-white/10 shadow-[0_0_40px_rgba(30,111,255,0.18)]">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F2C] via-[#0A0F2C]/50 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4">
                      <p className="text-xs uppercase tracking-widest text-[#CBD5E1]">Specialism</p>
                      <p className="font-heading font-semibold mt-1">{s.title}</p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>
        );
      })}

      {/* Unique CTA */}
      <section className="section-pad" data-testid="services-bottom-cta">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-cta-gradient p-12 md:p-14 text-center">
              <div className="absolute -top-32 -right-20 h-80 w-80 rounded-full bg-electric/40 blur-3xl" />
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Still deciding?</span>
              <h2 className="h-display text-5xl md:text-6xl mt-3">
                Not sure which service you need? Let's talk.
              </h2>
              <p className="mt-5 max-w-2xl mx-auto text-[#CBD5E1] text-lg">
                Send us a few sentences about what you're trying to do. We'll come back with the right path — even if it's not us.
              </p>
              <div className="mt-9 flex justify-center">
                <PrimaryButton to="/request-quote" testId="services-cta-quote">
                  Request a Free Quote
                </PrimaryButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      </div>
    </main>
  );
}
